import { components, GroupBase, SingleValueProps } from 'react-select';
import { Tooltip } from 'react-tooltip';
import moment from 'moment';
import { isEmpty } from 'lodash';

import {
  dateFormat,
  IUser,
  QTask,
  SelectOption,
  TASK_BLOCKED,
  TASK_COMPLETED,
  TASK_DELETED,
  TASK_IN_PROGRESS,
  TASK_NOT_STARTED,
  TASK_OVERDUE,
} from '@qwealth/qdata';
import { Box, colors, getPartnerShortCode } from '@qwealth/qcore';
import { getPathContents, S3Item, copyFiles } from '@qwealth/qvault';

import { AssignedWorkflow, AssignedWorkflowsFilters, AssignedWorkflowsQueryParams } from 'data/models/Workflows';
import { getTaskTemplateAttachmentsPath } from './workflowTemplatesHelper';
import { getTagKeys, getTaskAttachmentPath, isTaskDeleted } from './taskHelper';
import TaskStatusIcon from 'components/common/Activities/Task/TaskStatusIcon';
import { deleteTaskAttachments } from 'data/actions/activities';
import { DATE_FILTERS } from 'data/models/ActivityFilters';
import { caseInsensitiveFuzzySearch } from 'utils/search';
import { AppThunkDispatch } from 'data/store';
import { loadAssignedWorkflows, resetWorkflowsState, updateAssignedWorkflowsAction } from 'data/actions/workflows';
import { ALL_DATE_RANGE } from 'data/refData/acitivites';
import { ExtendedTaskStatus } from 'data/models/Activity';
import { FailedSettledResult } from './promiseHelper';
import { fetchAssignedWorkflow } from 'data/data-layer/workflows';

export const WORKFLOW_FILTERS_KEY = 'assigned_workflow_filters';

export const defaultAssignedWorkflowFilters = (isHouseholdPage: boolean, partnerId?: number, iaCode?: string): AssignedWorkflowsFilters => {
  return {
    filterDateRange: {
      fromDate: moment().startOf('day'),
      toDate: moment().add(7, 'day').endOf('day'),
    },
    filterDateValue: DATE_FILTERS[0].value,
    sortDirection: 'DESC',
    filterStatus: [],
    filterTags: [],
    filterPartnerId: isHouseholdPage ? undefined : partnerId,
    filterIaCode: isHouseholdPage ? undefined : iaCode,
    filterQuery: '',
  };
};

export const deleteAssignedWorkflowTasksAttachments = async (assignedWorkflow: AssignedWorkflow): Promise<void> => {
  const tasksToDelete = assignedWorkflow.groups
    .flatMap(group => group.tasksGroup)
    .filter(task => task.status !== TASK_COMPLETED && task.status !== TASK_IN_PROGRESS && !isTaskDeleted(task));
  if (tasksToDelete.length > 0) await Promise.all(tasksToDelete.map(task => deleteTaskAttachments(task)));
};

export const reloadWorkflowsDashboard = (
  dispatch: AppThunkDispatch,
  filters: AssignedWorkflowsFilters,
  hasPermission: boolean,
  householdQid?: string,
) => {
  const { filterPartnerId, filterIaCode } = filters;
  const hasHouseholdQid = !!householdQid;
  const hasPartner = filterPartnerId !== undefined;
  const canFilterByPartner = hasPermission && hasPartner;
  const canFilterByPartnerAndIaCode = hasPartner && !!filterIaCode;
  const canReload = hasHouseholdQid || canFilterByPartner || canFilterByPartnerAndIaCode;

  if (!canReload) {
    dispatch(resetWorkflowsState());
    return;
  }

  const queryParams = mapFiltersToQueryParams(filters, householdQid);
  dispatch(loadAssignedWorkflows(queryParams));
};
interface StatusStyles {
  borderColor: string;
  backgroundColor: string;
}

export const statusBackgroundColor = (status: ExtendedTaskStatus): StatusStyles => {
  switch (status) {
    case TASK_NOT_STARTED:
      return {
        borderColor: colors.gray[2],
        backgroundColor: colors.gray[4],
      };
    case TASK_IN_PROGRESS:
      return {
        borderColor: colors.blue,
        backgroundColor: colors.blueLight[0],
      };
    case TASK_BLOCKED:
      return {
        borderColor: colors.yellow,
        backgroundColor: colors.yellowLight[0],
      };
    case TASK_OVERDUE:
      return {
        borderColor: colors.red,
        backgroundColor: colors.redLight[0],
      };
    case TASK_COMPLETED:
      return {
        borderColor: colors.green,
        backgroundColor: colors.greenLight[0],
      };
    case TASK_DELETED:
      return {
        borderColor: colors.gray[2],
        backgroundColor: colors.gray[3],
      };
    default:
      return {
        borderColor: colors.gray[2],
        backgroundColor: colors.gray[4],
      };
  }
};

export const groupStatus = (tasks: QTask[]): string => {
  const activeTasks = tasks.filter(task => !isTaskDeleted(task));

  if (activeTasks.length === 0) {
    return TASK_DELETED;
  }

  if (activeTasks.every(task => task.status === TASK_COMPLETED)) {
    return TASK_COMPLETED;
  }
  if (activeTasks.some(task => task.status === TASK_IN_PROGRESS || task.status === TASK_COMPLETED)) {
    return TASK_IN_PROGRESS;
  }
  return TASK_NOT_STARTED;
};

export const copyTaskTemplateAttachmentsToTask = async (
  partnerShortCode: string,
  taskTemplateId: number,
  householdId: string,
  taskId: number,
): Promise<void> => {
  const templatePath = getTaskTemplateAttachmentsPath(partnerShortCode, taskTemplateId);
  const taskPath = getTaskAttachmentPath(householdId, taskId.toString());

  const templateContents: S3Item[] = await getPathContents(templatePath);
  if (!templateContents || templateContents.length === 0) {
    return;
  }

  const copyPromises = templateContents
    .filter(item => item.key && item.key.includes(templatePath))
    .map(item => {
      const currentPath = item.key!;
      const fileName = currentPath.replace(templatePath, '');
      const newPath = `${taskPath}${fileName}`;
      return copyFiles({ path: currentPath, newPath });
    });

  await Promise.all(copyPromises);
};

export const copyAttachmentsForAssignedWorkflowTasks = async (householdId: string, tasks: QTask[]): Promise<FailedSettledResult[]> => {
  const partnerShortCode = getPartnerShortCode(householdId);
  const failed: FailedSettledResult[] = [];

  for (const task of tasks) {
    if (task.taskTemplateId !== undefined && task.id !== undefined) {
      try {
        await copyTaskTemplateAttachmentsToTask(partnerShortCode, task.taskTemplateId, householdId, task.id);
      } catch (reason) {
        failed.push({ title: task.title, reason });
      }
    }
  }

  return failed;
};

export const statusSelectStyle = {
  control: (styles, state) => ({
    ...styles,
    height: '44px',
  }),
  indicatorSeparator: () => ({ display: 'none' }),
  dropdownIndicator: styles => ({
    ...styles,
    padding: '2px',
  }),

  option: (styles, { data, isFocused, isSelected }) => {
    const color = statusBackgroundColor(data.value).backgroundColor;
    return {
      ...styles,
      backgroundColor: isSelected ? color : isFocused ? color : undefined,

      ':active': {
        ...styles[':active'],
        backgroundColor: color,
      },
    };
  },
  valueContainer: styles => ({
    ...styles,
    padding: '2px',
  }),
  menuPortal: base => ({ ...base, zIndex: 9999 }),
};

export const statusSelectOption = props => (
  <components.Option {...props}>
    <Tooltip id="task-status" place="right" positionStrategy="fixed" />
    <Box data-tooltip-id="task-status" data-tooltip-content={props.data.value}>
      <TaskStatusIcon status={props.data.value as ExtendedTaskStatus} iconSize="28px" />
    </Box>
  </components.Option>
);

export const statusSelectSingleValue = (props: SingleValueProps<SelectOption<string>, false, GroupBase<SelectOption<string>>>) => (
  <components.SingleValue {...props}>
    <Box data-tooltip-id="task-status" data-tooltip-content={props.data.value}>
      <TaskStatusIcon status={props.data.value as ExtendedTaskStatus} iconSize="28px" />
    </Box>
  </components.SingleValue>
);

export const getAssignedWorkflowsSavedFilters = (isHouseholdPage: boolean, user: IUser): AssignedWorkflowsFilters => {
  const savedFilters = localStorage.getItem(WORKFLOW_FILTERS_KEY);
  if (savedFilters) {
    const preference = JSON.parse(savedFilters);
    return {
      ...preference,
      filterDateRange: {
        fromDate: moment(preference.filterDateRange.fromDate),
        toDate: moment(preference.filterDateRange.toDate),
      },
      filterPartnerId: isHouseholdPage ? undefined : preference.filterPartnerId,
      filterIaCode: isHouseholdPage ? undefined : preference.filterIaCode,
    };
  }

  return defaultAssignedWorkflowFilters(isHouseholdPage, user.partnerID);
};

export const assignedWorkflowTotalTasks = (assignedWorkflow: AssignedWorkflow): number => {
  return assignedWorkflow.groups.reduce((total, group) => total + group.tasksGroup.filter(task => !isTaskDeleted(task)).length, 0);
};

export const assignedWorkflowCompletedTasks = (assignedWorkflow: AssignedWorkflow): number => {
  return assignedWorkflow.groups.reduce(
    (completedTotal, group) => completedTotal + group.tasksGroup.filter(task => task.status === TASK_COMPLETED && !isTaskDeleted(task)).length,
    0,
  );
};

export const assignedWorkflowSearch = (query: string, assignedWorkflow: AssignedWorkflow): boolean =>
  caseInsensitiveFuzzySearch(query, [
    assignedWorkflow.name,
    assignedWorkflow.householdQid,
    assignedWorkflow.householdName,
    assignedWorkflow.tags ? getTagKeys(assignedWorkflow.tags).join(' ') : '',
  ]);

export const searchAssignedWorkflows = (
  assignedWorkflows: Array<AssignedWorkflow>,
  { filterQuery }: AssignedWorkflowsFilters,
): Array<AssignedWorkflow> => {
  if (!filterQuery) return assignedWorkflows;
  return assignedWorkflows.filter(assignedWorkflow => assignedWorkflowSearch(filterQuery, assignedWorkflow));
};

export const mapFiltersToQueryParams = (
  assignedWorkflowsFilterParam: AssignedWorkflowsFilters,
  householdQid?: string,
): AssignedWorkflowsQueryParams => {
  const { filterDateRange, filterDateValue, filterStatus, filterTags, filterPartnerId, filterIaCode, sortDirection } = assignedWorkflowsFilterParam;

  const filters: AssignedWorkflowsQueryParams = {
    'dueDate[orderby]': sortDirection,
    limit: 250,
  };

  if (householdQid) {
    filters.householdQid = householdQid;
  } else {
    if (filterIaCode) filters.iaCode = filterIaCode;
    if (filterPartnerId !== undefined) filters.partnerId = filterPartnerId;
  }

  if (!isEmpty(filterStatus)) {
    filters.status = filterStatus.toString();
  }

  if (!isEmpty(filterTags)) {
    filters.tags = filterTags.toString();
  }

  if (filterDateValue !== ALL_DATE_RANGE) {
    filters['dueDate[dateafter]'] = filterDateRange?.fromDate.format(dateFormat);
    filters['dueDate[datebefore]'] = filterDateRange?.toDate.format(dateFormat);
  }

  return filters;
};

export const fetchAndUpdateAssignedWorkflow = async (workflowId: number, dispatch): Promise<AssignedWorkflow | undefined> => {
  const data = await fetchAssignedWorkflow(workflowId, dispatch);
  if (data) {
    dispatch(updateAssignedWorkflowsAction(data));
  }
  return data;
};
